const EMPTY_DISPLAY = "未設定";

export const parseDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.includes(" ")
    ? raw.replace(" ", "T")
    : raw.replace(" ", "T");

  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const stripHtmlToPlain = (html) => {
  const plain = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain;
};

export const formatPublishedAt = (value) => {
  const d = parseDateTime(value);
  if (!d) return EMPTY_DISPLAY;

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

export const normalizeReleaseNoteListRow = (row) => ({
  id: row.id,
  title: row.title ?? "",
  content: row.content ?? "",
  publishedAt: row.published_at ?? null,
  isPublished: Boolean(row.is_published),
  isDraft: Boolean(row.is_draft),
  tags: Array.isArray(row?.meta_json?.tags)
    ? row.meta_json.tags
    : Array.isArray(row.tags)
      ? row.tags
      : row.tags
        ? [row.tags]
        : [],
});

export const normalizeTagsFromApi = (tagsRaw) => {
  if (!Array.isArray(tagsRaw)) return [];

  return tagsRaw
    .map((t, idx) => {
      if (typeof t === "string") {
        const name = t.trim();
        if (!name) return null;
        return { id: `tag-${idx}-${Date.now()}`, name };
      }

      if (t && typeof t === "object") {
        const name = typeof t.name === "string" ? t.name.trim() : "";
        if (!name) return null;
        return { id: t.id ?? `tag-${idx}-${Date.now()}`, name };
      }

      return null;
    })
    .filter(Boolean);
};

const fileNameFromPathOrUrl = (path, url) => {
  if (typeof path === "string" && path.trim()) {
    const parts = path.trim().split("/").filter(Boolean);
    const last = parts.pop();
    if (last) return last;
  }
  if (typeof url === "string" && url.trim()) {
    const trimmed = url.trim();
    try {
      const u = new URL(trimmed);
      const segs = u.pathname.split("/").filter(Boolean);
      const last = segs.pop();
      if (last) return last;
    } catch {
      const noQuery = trimmed.split("?")[0];
      const segs = noQuery.split("/").filter(Boolean);
      const last = segs.pop();
      if (last) return last;
    }
  }
  return "";
};

export const extractReleaseNoteImageDisplayItems = (data) => {
  if (!data || typeof data !== "object") return [];
  const raw = data.images ?? data.image_urls ?? data.imageUrls;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") {
        const url = item.trim();
        if (!url) return null;
        return {
          url,
          fileName: fileNameFromPathOrUrl("", url),
        };
      }
      if (item && typeof item === "object") {
        const url = String(item.url ?? item.src ?? "").trim();
        const path = String(item.path ?? "").trim();
        const resolvedUrl = url || path;
        if (!resolvedUrl) return null;
        const fileName =
          fileNameFromPathOrUrl(path, url) ||
          fileNameFromPathOrUrl("", resolvedUrl);
        return { url: url || resolvedUrl, fileName };
      }
      return null;
    })
    .filter(Boolean);
};

export const extractImageUrlsFromReleaseNoteDetail = (data) =>
  extractReleaseNoteImageDisplayItems(data).map((x) => x.url);

export const toLocalDatetimeInput = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  if (raw.includes("T")) {
    const next = raw.slice(0, 16);
    const d = new Date(next);
    return Number.isNaN(d.getTime()) ? "" : next;
  }

  const parts = raw.split(" ");
  if (parts.length !== 2) return "";
  const [datePart, timePartRaw] = parts;
  const timePart = String(timePartRaw).slice(0, 5);
  if (!datePart || !timePart) return "";
  return `${datePart}T${timePart}`;
};

export const toApiDatetimeString = (localDatetimeInput) => {
  if (!localDatetimeInput) return null;
  const raw = String(localDatetimeInput).trim();
  if (!raw) return null;

  if (!raw.includes("T")) return null;
  const [datePart, timePartRaw] = raw.split("T");
  const timePart = String(timePartRaw || "").slice(0, 5);
  if (!datePart || !timePart) return null;
  return `${datePart} ${timePart}:00`;
};

export const buildReleaseNotePayload = ({
  title,
  content,
  isPublished,
  publishedAtLocal,
  tags,
  redirectButtonText,
  redirectUrl,
}) => {
  const normalizedTags = Array.isArray(tags)
    ? tags
        .map((tag) => String(tag || "").trim())
        .filter(Boolean)
    : [];

  const payload = {
    mode: isPublished ? "publish" : "draft",
    title: String(title || "").trim(),
    content: content ?? "",
    meta_json: { tags: normalizedTags },
  };

  payload.url_context = String(redirectButtonText || "").trim();
  payload.url_path = String(redirectUrl || "").trim();

  if (isPublished) {
    const publishedAt = toApiDatetimeString(publishedAtLocal);
    if (!publishedAt) return null;
    payload.published_at = publishedAt;
  }

  return payload;
};

